import { CategoryResponse, ProductKind } from "@/web-client/api";
import { SearchQueryParameterController } from "@/hooks/useSearchQueryParameter";
import { FC, useCallback, useMemo } from "react";
import { ProductSearchQueryParameter } from "@/types/ProductSearchQueryParameter";
import MultiSelect from "@/components/MultiSelect";
import NavigationSection from "@/components/NavigationSection";
import NavigationSectionColor from "@/components/NavigationSectionColor";
import NavigationSectionFireProtectionMaterial from "@/components/NavigationSectionFireProtectionMaterial";
import NavigationSectionFireResistance from "@/components/NavigationSectionFireRegistance";
import NavigationSectionFlamePrevention from "@/components/NavigationSectionFlamePrevention";
import NavigationSectionFormaldehyde from "@/components/NavigationSectionFormaldehyde";
import NavigationSectionMaterial from "@/components/NavigationSectionMaterial";
import NavigationSectionPrice from "@/components/NavigationSectionPrice";
import NavigationSectionSize from "@/components/NavigationSectionSize";
import SearchBarMini from "@/components/SearchBarMini";
import { NavigationSectionSearchConditionGroups } from "@/features/search_product";

interface Props {
  categoryId?: number;
  query: ProductSearchQueryParameter;
  queryController: SearchQueryParameterController<ProductSearchQueryParameter>;
  categories: CategoryResponse[];
  kind?: ProductKind;
}

const NavigationProductSidebar: FC<Props> = ({
  categoryId,
  queryController,
  query,
  categories,
  kind,
}): JSX.Element => {
  const categorySelectChangeHandler = useCallback(
    (selected) => {
      queryController.addQuery("category_id", selected.value.toString());
      queryController.submitQuery();
    },
    [queryController],
  );

  const interiorCategories = useMemo<CategoryResponse[]>(
    () => categories.filter((c) => c.kind === ProductKind.INTERIOR),
    [categories],
  );

  const materialCategories = useMemo<CategoryResponse[]>(
    () => categories.filter((c) => c.kind === ProductKind.BUILDING_MATERIAL),
    [categories],
  );

  const onlyInterior = useMemo(
    () => kind === ProductKind.INTERIOR || !kind,
    [kind],
  );
  const onlyBuildingMaterial = useMemo(
    () => kind === ProductKind.BUILDING_MATERIAL || !kind,
    [kind],
  );

  return (
    <nav className="w-200 tracking-widest leading-loose laptop:block hidden pt-8">
      <div className="space-y-24">
        <SearchBarMini
          onSubmited={(keyword: string) => {
            queryController.addQuery("keyword", keyword);
            queryController.submitQuery();
          }}
          placeholder="建物や家具の名前で検索してみよう。"
        />

        {interiorCategories.length > 0 && onlyInterior && (
          <NavigationSection title={`家具のカテゴリ`} expandable={false}>
            <MultiSelect
              instanceId="interior-category"
              existEntities={interiorCategories}
              onChange={categorySelectChangeHandler}
              selectedId={categoryId}
            />
          </NavigationSection>
        )}

        {materialCategories.length > 0 && onlyBuildingMaterial && (
          <NavigationSection title={`建材のカテゴリ`} expandable={false}>
            <MultiSelect
              instanceId="material-category"
              existEntities={materialCategories}
              onChange={categorySelectChangeHandler}
              selectedId={categoryId}
            />
          </NavigationSection>
        )}

        <NavigationSectionMaterial
          queryController={queryController}
          query={query}
        />

        <NavigationSectionColor
          queryController={queryController}
          query={query}
        />

        {/* 詳細検索項目はカテゴリを選択しなければレンダリングされない */}
        {categoryId && (
          <NavigationSectionSearchConditionGroups
            queryController={queryController}
            query={query}
          />
        )}

        <NavigationSectionSize
          queryController={queryController}
          query={query}
        />

        <NavigationSectionPrice
          queryController={queryController}
          query={query}
        />

        {onlyBuildingMaterial && (
          <>
            <NavigationSectionFormaldehyde
              query={query}
              queryController={queryController}
            />
            <NavigationSectionFireProtectionMaterial
              query={query}
              queryController={queryController}
            />
            <NavigationSectionFlamePrevention
              query={query}
              queryController={queryController}
            />
            <NavigationSectionFireResistance
              query={query}
              queryController={queryController}
            />
          </>
        )}
      </div>
    </nav>
  );
};
export default NavigationProductSidebar;
